import React from 'react';
import translations from '../utils/translations';

const ICEditor = ({ 
  icName, 
  setIcName, 
  verticalBins, 
  setVerticalBins, 
  horizontalBins, 
  setHorizontalBins, 
  description, 
  setDescription, 
  pinLabels, 
  updateNetlistFirstLine, 
  generateNetlistFirstLine,
  shouldGenerateNetlistFirstLine,
  currentIC,
  setSavedICs,
  current_locale = 'en',
  isRTL = false
}) => {
  // Get translations based on locale
  const t = translations[current_locale] || translations['en'];
  return (
    <div style={{ 
      width: '100%',
      display: 'flex', 
      flexDirection: 'column',
      direction: isRTL ? 'rtl' : 'ltr',
      textAlign: isRTL ? 'right' : 'left'
    }}>
      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '15px',
        flexShrink: 0,
        backgroundColor: '#f8f9fa',
        padding: '12px 12px',
        borderRadius: '8px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
        border: '1px solid #eaeaea'
      }}>
        {/* Compact row for all inputs */}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: '10px',
          marginBottom: '10px',
          flexDirection: isRTL ? 'row-reverse' : 'row'
        }}>
          {/* IC Name input */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '5px',
            backgroundColor: 'white',
            padding: '8px 10px',
            borderRadius: '6px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #e6e6e6',
            width: '140px'
          }}>
            <label style={{ 
              fontWeight: '600', 
              fontSize: '14px', 
              color: '#2c3e50',
              marginBottom: '5px'
            }}>{t.icName}</label>
            <input
              type="text"
              value={icName}
              placeholder={t.enterICName}
              onChange={(e) => {
                const newName = e.target.value.replace(/\s+/g, ''); // Remove spaces
                setIcName(newName);
                
                // When IC name changes, only update the first line display without modifying netlist content
                setTimeout(() => {
                  // Generate the new first line with the updated name
                  const firstLine = generateNetlistFirstLine(pinLabels, newName);
                  // Only update the first line state, not the content
                  setNetlistFirstLine(firstLine);
                }, 0);
                
                // Update the current IC in state to reflect the name change in the list
                if (currentIC) {
                  setCurrentIC(prevIC => ({
                    ...prevIC,
                    name: newName
                  }));
                  
                  // Also update the IC in the savedICs list to reflect the change immediately
                  setSavedICs(prevICs => 
                    prevICs.map(ic => 
                      ic.id === currentIC.id ? { ...ic, name: newName } : ic
                    )
                  );
                }
              }}
              style={{ 
                padding: '6px 8px',
                borderRadius: '4px',
                border: '1px solid #cbd5e0',
                outline: 'none',
                transition: 'border 0.2s',
                fontSize: '14px',
                width: '100%'
              }}
              onFocus={(e) => e.target.style.border = '1px solid #3498db'}
              onBlur={(e) => e.target.style.border = '1px solid #cbd5e0'}
            />
          </div>
          
          {/* Vertical Pins input */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '5px',
            backgroundColor: 'white',
            padding: '8px 10px',
            borderRadius: '6px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #e6e6e6',
            width: '120px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '5px',
              flexDirection: isRTL ? 'row-reverse' : 'row'
            }}>
              <label style={{ 
                fontWeight: '600', 
                fontSize: '14px', 
                color: '#2c3e50'
              }}>
                {t.verticalPins}
              </label>
              <div style={{ 
                backgroundColor: '#ffebee',
                padding: '2px 5px',
                borderRadius: '4px',
                marginLeft: '5px',
                fontSize: '12px'
              }}>
                <span style={{ 
                  color: '#e53935', 
                  fontWeight: '500'
                }}>
                  {t.max} 16
                </span>
              </div>
            </div>
            <input
              type="number"
              min="0"
              max="16"
              value={verticalBins}
              onChange={(e) => {
                const newValue = Math.min(16, Math.max(0, parseInt(e.target.value || 0)));
                setVerticalBins(newValue);
                
                // Enable auto-generation when user changes pin count
                shouldGenerateNetlistFirstLine.current = true;
                
                // Update the NetList first line
                setTimeout(() => updateNetlistFirstLine(pinLabels), 10);
              }}
              style={{ 
                padding: '6px 8px',
                borderRadius: '4px',
                border: '1px solid #cbd5e0',
                outline: 'none',
                transition: 'border 0.2s',
                fontSize: '14px',
                width: '100%'
              }}
              onFocus={(e) => e.target.style.border = '1px solid #3498db'}
              onBlur={(e) => e.target.style.border = '1px solid #cbd5e0'}
            />
          </div>
          
          {/* Horizontal Pins input */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '5px',
            backgroundColor: 'white',
            padding: '8px 10px',
            borderRadius: '6px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #e6e6e6',
            width: '120px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '5px',
              flexDirection: isRTL ? 'row-reverse' : 'row'
            }}>
              <label style={{ 
                fontWeight: '600', 
                fontSize: '14px', 
                color: '#2c3e50'
              }}>
                {t.horizontalPins}
              </label>
              <div style={{ 
                backgroundColor: '#ffebee',
                padding: '2px 5px',
                borderRadius: '4px',
                marginLeft: '5px',
                fontSize: '12px'
              }}>
                <span style={{ 
                  color: '#e53935', 
                  fontWeight: '500'
                }}>
                  {t.max} 16
                </span>
              </div>
            </div>
            <input
              type="number"
              min="0"
              max="16"
              value={horizontalBins}
              onChange={(e) => {
                const newValue = Math.min(16, Math.max(0, parseInt(e.target.value || 0)));
                setHorizontalBins(newValue);
                
                // Enable auto-generation when user changes pin count
                shouldGenerateNetlistFirstLine.current = true;
                
                // Update the NetList first line
                setTimeout(() => updateNetlistFirstLine(pinLabels), 10);
              }}
              style={{ 
                padding: '6px 8px',
                borderRadius: '4px',
                border: '1px solid #cbd5e0',
                outline: 'none',
                transition: 'border 0.2s',
                fontSize: '14px',
                width: '100%'
              }}
              onFocus={(e) => e.target.style.border = '1px solid #3498db'}
              onBlur={(e) => e.target.style.border = '1px solid #cbd5e0'}
            />
          </div>
        </div>
        
        {/* Info box below inputs */}
        <div style={{
          padding: '6px 10px',
          background: 'linear-gradient(90deg, #e3f6ff 80%, #f7fafc 100%)',
          borderRadius: '8px',
          border: '1px solid #c3e6fc',
          color: '#256188',
          fontSize: '0.93em',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '9px',
          width: '100%',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
        }}>
          <ul style={{
            margin: 0,
            padding: '0 0 0 18px',
            listStyleType: 'disc',
            lineHeight: '1.4',
            fontWeight: 400
          }}>
            <li style={{ fontSize: '0.93em', fontStyle: 'italic', color: '#285377', opacity: 0.95 }}>{t.pinLabelsEditable}</li>
            <li style={{ fontSize: '0.93em', fontStyle: 'italic', color: '#285377', opacity: 0.95 }}>{t.pinCheckboxes}</li>
            <li style={{ fontSize: '0.93em', fontStyle: 'italic', color: '#285377', opacity: 0.95 }}>{t.pinEraseToRemove}</li>
          </ul>
        </div>
      </div>

      <div style={{ 
        marginTop: '10px', 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: '120px',
        overflow: 'visible',
        marginBottom: '0'
      }}>
        <label style={{ 
          marginBottom: '5px',
          fontWeight: '600',
          fontSize: '14px',
          color: '#2c3e50'
        }}>{t.description}</label>
        <textarea 
          style={{ 
            width: '100%', 
            flex: 1,
            padding: '10px', 
            boxSizing: 'border-box', 
            resize: 'none',
            borderRadius: '4px',
            border: '1px solid #ddd',
            minHeight: '80px'
          }}
          placeholder={t.enterICDescription}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
    </div>
  );
};

export default ICEditor;
